/**
 * MapView Component
 * 
 * Displays an interactive global map with location markers for guests, team members, and clients.
 * Provides filtering options and location search functionality.
 */
import React, { useEffect, useRef, useState } from 'react';

// Third-party libraries
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

// Local components
import { LocationSearch } from './LocationSearch';
import { ErrorBoundary } from '@/shared/components/feedback/ErrorBoundary';

// Hooks
import { useMap } from './useMap';

// Types
import { MapDataPoint } from '@/services/api/map';

// MapBox token from environment variables - using Vite's import.meta.env
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || '';
console.log('MAPBOX_TOKEN in MapView:', MAPBOX_TOKEN);

/**
 * Map marker colors by entity type
 */
const MARKER_COLORS = {
  guest: '#4F46E5', // Indigo for guests
  team: '#10B981',  // Green for teams
  client: '#F59E0B' // Amber for clients
} as const;

/**
 * Props for the LegendItem component
 */
interface LegendItemProps {
  /** Color for the legend item marker */
  color: string;
  /** Label text for the legend item */
  label: string;
  /** Optional count to display next to the label */
  count?: number;
}

/**
 * Map legend item component
 * 
 * @param props - Component props
 * @returns React component
 */
const LegendItem: React.FC<LegendItemProps> = ({ color, label, count }) => (
  <div className="flex items-center mb-2">
    <div className={`w-4 h-4 rounded-full mr-2`} style={{ backgroundColor: color }}></div>
    <span className="text-sm text-gray-700">
      {label} {count !== undefined && `(${count})`}
    </span>
  </div>
);

/**
 * Create a map marker element
 * 
 * @param point - Map data point
 * @param onClick - Click handler for the marker
 * @returns HTML element for the marker
 */
const createMarkerElement = (point: MapDataPoint, onClick: () => void): HTMLDivElement => {
  // Ensure we have a valid color or default to guest color
  const markerColor = point.type && point.type in MARKER_COLORS 
    ? MARKER_COLORS[point.type as keyof typeof MARKER_COLORS] 
    : MARKER_COLORS.guest;
  
  // Create marker element
  const markerEl = document.createElement('div');
  markerEl.className = 'marker';
  markerEl.style.width = '20px';
  markerEl.style.height = '20px';
  markerEl.style.borderRadius = '50%';
  markerEl.style.backgroundColor = markerColor;
  markerEl.style.border = '2px solid white';
  markerEl.style.cursor = 'pointer';
  markerEl.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
  
  // Add click event to marker
  markerEl.addEventListener('click', onClick);
  
  return markerEl;
};

/**
 * Create popup HTML content
 * 
 * @param point - Map data point
 * @returns HTML string for popup content
 */
const createPopupContent = (point: MapDataPoint): string => {
  return `
    <div>
      <h3 class="font-bold">${point.name || 'Unknown'}</h3>
      <p>${point.title || ''} ${point.company ? `at ${point.company}` : ''}</p>
      <p class="text-xs text-gray-500">${point.type || 'location'}</p>
    </div>
  `;
};

/**
 * MapView Component
 * 
 * Displays an interactive global map with location markers for guests, team members, and clients.
 * Provides filtering options and location search functionality.
 * 
 * @returns React component
 */
const MapView: React.FC = () => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [zoom, setZoom] = useState<number>(1.5);
  const { mapData, isLoading, allDataPoints, selectedLocation, setSelectedLocation } = useMap();
  const [showGuests, setShowGuests] = useState<boolean>(true);
  const [showTeams, setShowTeams] = useState<boolean>(true);
  const [showClients, setShowClients] = useState<boolean>(true);

  // Initialize map when component mounts
  useEffect(() => {
    if (map.current || !mapContainer.current) return;

    console.log('Initializing Mapbox with token:', MAPBOX_TOKEN);
    mapboxgl.accessToken = MAPBOX_TOKEN;
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/dark-v10',
      center: [0, 20],
      zoom: zoom,
      projection: 'globe'
    });

    // Add navigation controls
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Clean up on unmount
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [zoom]);

  // Update markers when data changes
  useEffect(() => {
    if (!map.current || isLoading || !allDataPoints || allDataPoints.length === 0) return;

    // Remove existing markers
    const markers = document.querySelectorAll('.mapboxgl-marker');
    markers.forEach(marker => marker.remove());

    // Add new markers based on filters
    allDataPoints.forEach(point => {
      if (!point || !point.location || typeof point.location.lat !== 'number' || typeof point.location.lng !== 'number') {
        return; // Skip invalid points
      }
      
      // Skip if filtered out
      if (
        (point.type === 'guest' && !showGuests) ||
        (point.type === 'team' && !showTeams) ||
        (point.type === 'client' && !showClients)
      ) {
        return;
      }

      // Create marker element
      const markerEl = createMarkerElement(point, () => {
        if (point.id) {
          setSelectedLocation(point.id);
        }
      });

      // Create popup
      const popup = new mapboxgl.Popup({ offset: 25 })
        .setHTML(createPopupContent(point));

      // Add marker to map
      new mapboxgl.Marker(markerEl)
        .setLngLat([point.location.lng, point.location.lat])
        .setPopup(popup)
        .addTo(map.current!);
    });
  }, [mapData, isLoading, allDataPoints, showGuests, showTeams, showClients, setSelectedLocation]);

  /**
   * Toggle visibility of guest markers
   */
  const toggleGuests = (): void => setShowGuests(!showGuests);
  
  /**
   * Toggle visibility of team markers
   */
  const toggleTeams = (): void => setShowTeams(!showTeams);
  
  /**
   * Toggle visibility of client markers
   */
  const toggleClients = (): void => setShowClients(!showClients);

  return (
    <ErrorBoundary>
      <div className="h-full flex flex-col">
        <div className="p-4 bg-white shadow-sm z-10">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Global Map</h1>
          <div className="flex flex-wrap gap-4 mb-4">
            <LocationSearch />
            <div className="flex items-center space-x-4">
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showGuests}
                  onChange={toggleGuests}
                  className="form-checkbox h-5 w-5 text-indigo-600"
                />
                <span className="ml-2 text-gray-700">Guests</span>
              </label>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showTeams}
                  onChange={toggleTeams}
                  className="form-checkbox h-5 w-5 text-green-600"
                />
                <span className="ml-2 text-gray-700">Teams</span>
              </label>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showClients}
                  onChange={toggleClients}
                  className="form-checkbox h-5 w-5 text-amber-600"
                />
                <span className="ml-2 text-gray-700">Clients</span>
              </label>
            </div>
          </div>
        </div>

        <div className="relative flex-1">
          {/* Map container */}
          <div ref={mapContainer} className="absolute inset-0" />

          {/* Map legend */}
          <div className="absolute bottom-4 left-4 bg-white p-3 rounded-lg shadow-md z-10">
            <h3 className="font-bold mb-2 text-gray-900">Legend</h3>
            <LegendItem 
              color={MARKER_COLORS.guest} 
              label="Guests" 
              count={mapData?.guests?.length || 0} 
            />
            <LegendItem 
              color={MARKER_COLORS.team} 
              label="Teams" 
              count={mapData?.teams?.length || 0} 
            />
            <LegendItem 
              color={MARKER_COLORS.client} 
              label="Clients" 
              count={mapData?.clients?.length || 0} 
            />
          </div>

          {/* Loading indicator */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 z-20">
              <div className="bg-white p-4 rounded-lg shadow-lg">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500 mx-auto mb-2"></div>
                <p className="text-gray-700">Loading map data...</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default MapView;
